// src/contexts/AuthContext.tsx
import React, { createContext, useState, useEffect, useContext } from 'react';
import { User } from '../types';
import api from '../services/api';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  googleLogin: (credential: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  isAuthenticated: false,
  login: async () => false,
  googleLogin: async () => false,
  logout: () => {},
  loading: false,
});

// Auth Provider
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          // Verify token and get user info
          const userData = await api.get<any>('/api/auth/me', token);
          setUser(userData.user || userData);
          setIsAuthenticated(true);
        } catch (error) {
          console.error('Token validation failed:', error);
          localStorage.removeItem('token');
          setToken(null);
          setIsAuthenticated(false);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [token]);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await api.post<{ accessToken: string; user: User }>('/api/auth/login', { email, password });

      if (response.accessToken && response.user) {
        setToken(response.accessToken);
        setUser(response.user);
        setIsAuthenticated(true);
        localStorage.setItem('token', response.accessToken);
        return true;
      }

      throw new Error('Invalid response from server');
    } catch (error) {
      console.error('Login failed:', error);
      setIsAuthenticated(false);
      return false;
    }
  };

  const googleLogin = async (credential: string): Promise<boolean> => {
    try {
      const response = await api.post<{ accessToken: string; user: User }>('/api/auth/google', { credential });

      if (response.accessToken && response.user) {
        setToken(response.accessToken);
        setUser(response.user);
        setIsAuthenticated(true);
        localStorage.setItem('token', response.accessToken);
        return true;
      }

      throw new Error('Invalid response from server');
    } catch (error) {
      console.error('Google login failed:', error);
      setIsAuthenticated(false);
      return false;
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
    localStorage.removeItem('token');
  };

  return (
    <AuthContext.Provider value={{ user, token, isAuthenticated, login, googleLogin, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// useAuth hook
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};